import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Button, Icon, Input, TitleSecondary } from '../../components';

import { formatMoney } from '../../utils';

import styles from './clients.module.scss';

const VirtualWallet = ({
  topUpAmount,
  onTopUpAmountChanged,
  topUpClientWallet,
  business_clients,
  virtual_balance,
  payment_cards,
}) => {
  const [showAllPaymentMethods, setShowAllPaymentMethods] = useState(false);

  const paymentMethods = useMemo(() => {
    const businessWallets = business_clients.map(client => ({
      key: `business-${client.id}`,
      type: 'business',
      title: 'Verslo piniginė',
      subtitle: client.name,
      value: formatMoney(client.remaining_credit),
    }));

    const cards = payment_cards
      .filter(card => card.number.includes('****'))
      .map(card => ({
        key: `card-${card.id}`,
        type: 'card',
        title: card.number,
        subtitle: card.company_name || '',
        value: card.expiration_date || '',
      }));

    return [...businessWallets, ...cards];
  }, [business_clients, payment_cards]);

  const visiblePaymentMethods = showAllPaymentMethods
    ? paymentMethods
    : paymentMethods.slice(0, 5);

  const hiddenPaymentMethodsCount =
    paymentMethods.length - visiblePaymentMethods.length;

  return (
    <div className={styles.clientInfoContainer}>
      <TitleSecondary>Virtuali piniginė</TitleSecondary>
      <div className={styles.walletHistory}>
        <div className={styles.personalWallet}>
          <div className={styles.personalWalletHeader}>
            <span className={styles.personalWalletTitle}>
              Asmeninė piniginė
            </span>
            <span className={styles.personalWalletBalance}>
              {formatMoney(virtual_balance)}
            </span>
          </div>
          <div className={styles.personalWalletForm}>
            <Input
              name="topUpAmount"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              onChange={e => onTopUpAmountChanged(e)}
              value={topUpAmount || ''}
            />
            <Button
              color={topUpAmount ? 'primary' : 'outline'}
              disabled={!topUpAmount}
              onClick={topUpClientWallet}
            >
              Papildyti
            </Button>
          </div>
        </div>
        {visiblePaymentMethods.map(method => (
          <div key={method.key} className={styles.walletHistoryElement}>
            <div className={styles.walletHistoryElementDetails}>
              <span className={styles.walletHistoryElementDetailsTitle}>
                {method.title}
              </span>
              {method.subtitle && (
                <span className={styles.walletHistoryElementDetailsSubtitle}>
                  {method.subtitle}
                </span>
              )}
            </div>
            {method.value && (
              <span className={styles.walletHistoryElementValue}>
                {method.value}
              </span>
            )}
          </div>
        ))}
        {paymentMethods.length > 5 && (
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={() => setShowAllPaymentMethods(prev => !prev)}
          >
            {showAllPaymentMethods
              ? 'Rodyti mažiau'
              : `Rodyti daugiau (${hiddenPaymentMethodsCount})`}
            <Icon
              name="chevron-down"
              classes={{
                icon: `${styles.showMoreButtonIcon} ${
                  showAllPaymentMethods ? styles.showMoreButtonIconExpanded : ''
                }`,
              }}
            />
          </button>
        )}
        {payment_cards.map(card => {
          if (card.number.includes('****')) {
            return null;
          }

          return (
            <div key={card.id} className={styles.personalWallet}>
              <div className={styles.personalWalletHeader}>
                <span className={styles.personalWalletTitle}>
                  {card.number}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

VirtualWallet.propTypes = {
  topUpAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
  onTopUpAmountChanged: PropTypes.func.isRequired,
  topUpClientWallet: PropTypes.func.isRequired,
  payment_cards: PropTypes.arrayOf(
    PropTypes.shape({
      card_type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      expiration_date: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      company_name: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      id: PropTypes.number,
      number: PropTypes.string,
    })
  ).isRequired,
  business_clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      remaining_credit: PropTypes.number.isRequired,
    })
  ).isRequired,
  virtual_balance: PropTypes.number.isRequired,
};

export default connect()(VirtualWallet);
